// Silent re-login. Shopify customer sessions die after ~24h; while logged in
// we establish a long-lived httpOnly refresh cookie on the vetstor-app domain
// (one redirect hop; slides on use). On expiry, refresh in the background:
// fetch <authOrigin>/session/refresh?format=json (same-site in prod, so the
// Lax cookie rides along), then fetch the returned Multipass URL (Set-Cookie
// lands without following the 302) and reload in place. Falls back to the
// top-level redirect bounce when fetch can't carry the cookie (cross-site
// staging, or iOS WKWebView where ITP blocks the third-party cookie, DEV-3028)
// or errors. The 'vetstor_rl' hint holds the customer id, not just
// a flag — on presence alone, a second account logging in behind the first
// keeps the first one's refresh cookie and re-logs-in as the wrong customer.
(function () {
  'use strict';

  // Storefront handoff (DEV-2861) owns this client: the pet profile lives on the headless
  // storefront and auth is the storefront vs_session, so legacy multipass/establish here would
  // fight it (and re-mint a refresh family after SSO logout). Set in <head> by
  // storefront-handoff.liquid; UNSET when storefront_origin is blank.
  if (window.theme && window.theme.storefrontHandoffEligible) return;

  if (window.Shopify && window.Shopify.designMode) return;
  if (window.top !== window.self) return;

  // In-app guest on the pet profile: the shop page has nothing to show, and the app's login
  // recovery (safariHandoff isAccountLoginUrl) only fires on shop /account/login, so every miss
  // goes there instead of staying quietly logged out. While vetstor-app's legacy block
  // (#react-profile) still renders, its inline gate ran first and owns this page.
  var theme = window.theme || {};
  var profilePath = typeof theme.petProfilePath === 'function' ? theme.petProfilePath(window.location.pathname) : null;
  var gate =
    !!profilePath &&
    !!theme.isNativeApp &&
    !(theme.customer && theme.customer.id) &&
    !document.getElementById('react-profile');
  var gateDone = false;
  var HINT_COOKIE = 'vetstor_rl';

  function anonDistinctId() {
    var name = 'ph_' + theme.posthogKey + '_posthog';
    try {
      var stored = JSON.parse(localStorage.getItem(name) || 'null');
      if (stored && stored.distinct_id) return String(stored.distinct_id);
    } catch (e) {
      /* ignore */
    }
    try {
      var m = new RegExp('(?:^|;\\s*)' + name + '=([^;]*)').exec(document.cookie);
      var c = m ? JSON.parse(decodeURIComponent(m[1])) : null;
      if (c && c.distinct_id) return String(c.distinct_id);
    } catch (e) {
      /* ignore */
    }
    return 'anon-native-gate';
  }

  function captureGate(action, why) {
    if (typeof theme.posthogBeacon !== 'function') return;
    theme.posthogBeacon('pet_profile_gate', anonDistinctId(), {
      action: action,
      why: why,
      hasHint: !!getCookie(HINT_COOKIE),
      isNativeApp: true,
    });
  }

  function gateToLogin(why) {
    gateDone = true;
    captureGate('to_login', why);
    window.location.replace('/account/login?return_to=' + encodeURIComponent(profilePath));
  }

  var cfg = window.theme && window.theme.silentRelogin;
  if (!cfg || !cfg.authOrigin) {
    if (gate) gateToLogin('no_origin');
    return;
  }
  // Staff "Login as Customer": never mint a 400-day family for a customer the
  // staff member is only viewing. Set in <head> by datalayer-user.liquid.
  if (window.vetstorIsImpersonated || document.cookie.indexOf('vetstor_imp=1') !== -1) {
    if (gate) gateToLogin('impersonated');
    return;
  }

  // Matches the refresh-token inactivity window (Chrome caps cookies at 400d).
  // Safari ITP caps JS-set cookies at 7d regardless — re-set on every
  // logged-in page view below so the hint slides with activity.
  var HINT_DAYS = 400;
  var THROTTLE_KEY = 'vetstorReloginAttemptAt';
  // Separate key: an establish attempt must not suppress a legitimate refresh.
  var ESTABLISH_KEY = 'vetstorEstablishAttemptAt'; // app.js logout handler clears it
  var THROTTLE_MS = 10 * 60 * 1000;
  var GATE_THROTTLE_MS = 60 * 1000;
  // Renew under Shopify's ~24h session, before the reactive path's flash.
  var AUTH_AT_KEY = 'vetstorAuthAt';
  var RENEW_AFTER_MS = 20 * 60 * 60 * 1000;

  function throttled(key, windowMs) {
    var last = 0;
    try {
      last = parseInt(sessionStorage.getItem(key) || '0', 10) || 0;
    } catch (e) {
      /* sessionStorage unavailable */
    }
    return Date.now() - last < (windowMs || THROTTLE_MS);
  }

  function markAttempt(key) {
    try {
      sessionStorage.setItem(key, String(Date.now()));
    } catch (e) {
      /* ignore */
    }
  }

  function authAt() {
    try {
      return parseInt(localStorage.getItem(AUTH_AT_KEY) || '0', 10) || 0;
    } catch (e) {
      return 0;
    }
  }

  function markAuth() {
    try {
      localStorage.setItem(AUTH_AT_KEY, String(Date.now()));
    } catch (e) {
      /* ignore */
    }
  }

  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
    return match ? match[1] : null;
  }

  function setHint(customerId) {
    document.cookie =
      HINT_COOKIE + '=' + customerId + '; path=/; max-age=' + HINT_DAYS * 86400 + '; secure; samesite=lax';
  }

  function clearHint() {
    document.cookie = HINT_COOKIE + '=; path=/; max-age=0; secure; samesite=lax';
  }

  function currentTarget() {
    return window.location.pathname + window.location.search + window.location.hash;
  }

  // Renew while still logged in — no reload needed. Shares THROTTLE_KEY with
  // the reactive path so the two can't double-rotate the same token.
  function maybeRenew() {
    var last = authAt();
    if (!last) {
      markAuth(); // unknown age — start the clock, don't renew
      return;
    }
    if (Date.now() - last < RENEW_AFTER_MS) return;
    if (throttled(THROTTLE_KEY)) return;
    markAttempt(THROTTLE_KEY);

    fetch(cfg.authOrigin + '/session/refresh?format=json&return_to=' + encodeURIComponent(currentTarget()), {
      credentials: 'include',
    })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (data) {
        if (!data || !data.ok || !data.multipassUrl) return;
        if (data.multipassUrl.indexOf(window.location.origin + '/') !== 0) return;
        return fetch(data.multipassUrl, { redirect: 'manual', credentials: 'include' }).then(markAuth);
      })
      .catch(function () {
        /* renewal is best-effort — the reactive path still covers expiry */
      });
  }

  // Auth/checkout/reset flows: replacing the document (even a "silent"
  // reload) discards mid-flow state — attempt nothing here, the next
  // ordinary page view re-logs-in anyway. Must stay AFTER the
  // relogin=failed check below so the hint still clears on the login page.
  var SKIP_PATHS =
    /^\/(account\/(login|register|recover|reset|activate|invalid_token)|challenge|password|checkouts?\b)/;

  // Top-level nav to the auth origin makes it first-party, so the refresh cookie
  // is attached — the only ITP-proof recovery on iOS WKWebView, where the
  // cross-origin fetch above can't carry it (DEV-3028). Runs in the native app
  // too: the shell allowlists auth-origin navigations and keeps them in the
  // WebView (useStoreWebView isAuthOriginUrl), so it does not eject to Safari.
  function redirectRefresh() {
    window.location.replace(
      cfg.authOrigin + '/session/refresh?return_to=' + encodeURIComponent(currentTarget()) + (gate ? '&src=native_gate' : '')
    );
  }

  function gateBounce(why) {
    gateDone = true;
    captureGate('bounce', why);
    redirectRefresh();
  }

  // Set by the app.js logout handler. Clears only on a fresh logged-in load —
  // bfcache/visibility re-runs can see a stale pre-logout document.
  var LOGGED_OUT_KEY = 'vetstorLoggedOut';

  function loggedOutLatched() {
    try {
      return !!localStorage.getItem(LOGGED_OUT_KEY);
    } catch (e) {
      return false; // storage unavailable — never lock out
    }
  }

  function run(freshLoad) {
    if (gateDone) return;
    var customerId = String((window.theme.customer && window.theme.customer.id) || '');
    var loggedIn = !!customerId;

    if (loggedIn && freshLoad) {
      try {
        localStorage.removeItem(LOGGED_OUT_KEY);
      } catch (e) {
        /* ignore */
      }
    }

    // Refresh failed server-side — drop the hint so we stop trying.
    if (window.location.search.indexOf('relogin=failed') !== -1) {
      clearHint();
      if (gate) gateToLogin('relogin_failed');
      return;
    }

    if (SKIP_PATHS.test(window.location.pathname)) return;

    if (loggedIn) {
      // Only skip when the refresh cookie already belongs to THIS customer.
      // A mismatch (another account logged in behind this one, or a legacy '1'
      // hint) falls through to establish, whose claim overwrites the cookie.
      if (getCookie(HINT_COOKIE) === customerId) {
        // Slide the hint window (Safari's 7-day JS-cookie cap restarts here).
        setHint(customerId);
        maybeRenew();
        return;
      }
      // Stale document re-run mid-logout (hint already cleared) — don't re-mint.
      if (!freshLoad && loggedOutLatched()) return;
      // A failing claim leaves no hint — without this, one pending row per view.
      if (throttled(ESTABLISH_KEY)) return;
      markAttempt(ESTABLISH_KEY);
      fetch('/a/v/api/session/establish?return_to=' + encodeURIComponent(currentTarget()), { method: 'POST' })
        .then(function (response) {
          return response.ok ? response.json() : null;
        })
        .then(function (data) {
          if (!data || !data.claimUrl || data.claimUrl.indexOf(cfg.authOrigin + '/') !== 0) return;
          // Same-site in prod, so Set-Cookie lands without navigating.
          return fetch(data.claimUrl + '&format=json', { credentials: 'include' })
            .then(function (response) {
              return response.ok ? response.json() : null;
            })
            .then(function (claim) {
              if (claim && claim.ok) {
                setHint(customerId);
                markAuth();
                try {
                  localStorage.removeItem(LOGGED_OUT_KEY);
                } catch (e) {
                  /* ignore */
                }
                return;
              }
              // The fetch claim couldn't write the cookie (iOS WebKit/ITP blocks
              // the third-party Set-Cookie) — do the claim as a top-level nav so
              // it lands first-party. Hint set before the hop; self-heals via
              // ?relogin=failed. Stays in the WebView (auth-origin allowlist).
              setHint(customerId);
              window.location.replace(data.claimUrl);
            })
            .catch(function () {
              setHint(customerId);
              window.location.replace(data.claimUrl);
            });
        })
        .catch(function () {
          /* network hiccup — retry naturally on a later page view */
        });
      return;
    }

    // User logged out here — don't resurrect the session if the revoke failed.
    if (loggedOutLatched()) {
      if (gate) gateToLogin('logged_out');
      return;
    }

    // Ordinary pages need no hint — Safari drops JS cookies at 7d; the refresh cookie outlives it.
    var hadHint = !!getCookie(HINT_COOKIE);
    if (gate && !hadHint) {
      gateToLogin('no_hint');
      return;
    }

    if (throttled(THROTTLE_KEY, gate ? GATE_THROTTLE_MS : THROTTLE_MS)) {
      if (gate) gateToLogin('throttle');
      return;
    }
    markAttempt(THROTTLE_KEY);
    if (gate) gateDone = true;

    fetch(
      cfg.authOrigin +
        '/session/refresh?format=json&return_to=' +
        encodeURIComponent(currentTarget()) +
        (gate ? '&src=native_gate' : ''),
      { credentials: 'include' }
    )
      .then(function (response) {
        if (!response.ok) {
          var err = new Error('refresh http ' + response.status);
          err.httpStatus = response.status;
          throw err;
        }
        return response.json();
      })
      .then(function (data) {
        if (data && data.ok && data.multipassUrl && data.multipassUrl.indexOf(window.location.origin + '/') === 0) {
          // Redeem Multipass without navigating: Set-Cookie on the 302 is
          // stored even with redirect:'manual'; reload picks up the session.
          return fetch(data.multipassUrl, { redirect: 'manual', credentials: 'include' }).then(function () {
            markAuth();
            window.location.reload();
          });
        }
        if (data && data.reason === 'invalid') {
          // Token is dead server-side — stop trying, stay quietly logged out.
          clearHint();
          if (gate) gateToLogin('invalid');
          return;
        }
        // Cookie didn't ride the fetch (cross-site staging) or odd payload —
        // the top-level bounce still carries Lax cookies. Hint-only: without
        // one, no_cookie is just an anonymous visitor.
        if (gate) gateBounce('json_miss');
        else if (hadHint) redirectRefresh();
      })
      .catch(function (err) {
        // Auth-origin HTTP error: shop login, not a bounce onto the failing host.
        if (gate) {
          if (err && err.httpStatus) gateToLogin('http_error');
          else gateBounce('itp');
          return;
        }
        if (hadHint) redirectRefresh();
      });
  }

  run(true);

  // A page view is not the only wake-up in a webview: the app keeps one
  // document alive across days of backgrounding, so the session can die with
  // nothing scheduled to notice. Throttles bound the cost of re-running.
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    gateDone = false;
    run();
  });
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') run();
  });
})();
